import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  CheckCircle2, 
  Plane, 
  Hotel, 
  Car, 
  Copy, 
  Check,
  MapPin,
  Calendar,
  Users,
  CreditCard,
  Download,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { Money } from '../contexts/CurrencyContext';

export const HolidayBookingSuccess = ({ sessionId, bookingRef, onGoHome }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [error, setError] = useState(null);
  const [copiedRef, setCopiedRef] = useState(null);

  useEffect(() => {
    const confirmBooking = async () => {
      try {
        setIsLoading(true);
        
        // Confirm the booking and get reference numbers
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/holidays/confirm-booking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            session_id: sessionId,
            booking_ref: bookingRef 
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          setBookingData(result);
          toast.success('Booking confirmed! Your reference numbers are ready.');
        } else {
          setError(result.error || 'Failed to confirm booking');
        }
      } catch (err) {
        console.error('Confirmation error:', err);
        setError('Failed to confirm booking. Please contact support.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (sessionId || bookingRef) {
      confirmBooking();
    }
  }, [sessionId, bookingRef]);

  const copyToClipboard = async (text, label) => {
    if (!text) {
      toast.error(`No ${label} to copy`);
      return;
    }
    
    try {
      // Try modern clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for HTTP or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
        } catch (execErr) {
          console.warn('execCommand failed:', execErr);
        }
        
        document.body.removeChild(textArea);
      }
      
      setCopiedRef(label);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopiedRef(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast.info(`Copy manually: ${text}`, { duration: 10000 });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-700">Confirming your booking...</h2>
          <p className="text-slate-500 mt-2">Generating your reference numbers</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-red-700 mb-2">Booking Issue</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={onGoHome}>Return Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const refs = bookingData?.references || {};
  const details = bookingData?.details || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle2 className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Booking Confirmed!</h1>
          <p className="text-slate-600">Your holiday package has been successfully booked</p>
        </div>

        {/* Main Booking Reference */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Booking Reference</p>
                <p className="text-2xl font-bold text-white tracking-wider">{refs.booking}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={() => copyToClipboard(refs.booking, 'Booking Reference')}
              >
                {copiedRef === 'Booking Reference' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="grid gap-4">
              {/* Destination */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <MapPin className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm text-slate-500">Destination</p>
                  <p className="font-semibold text-slate-800">{details.destination}</p>
                </div>
              </div>
              
              {/* Dates */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Calendar className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm text-slate-500">Travel Dates</p>
                  <p className="font-semibold text-slate-800">{details.checkin} → {details.checkout}</p>
                </div>
              </div>
              
              {/* Hotel */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Hotel className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm text-slate-500">Hotel</p>
                  <p className="font-semibold text-slate-800">{details.hotel_name}</p>
                </div>
              </div>
              
              {/* Total */}
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <CreditCard className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm text-emerald-600">Total Paid</p>
                  <p className="font-bold text-emerald-700 text-lg"><Money gbp={details.total_price?.toLocaleString()} /></p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reference Numbers Grid */}
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Your Reference Numbers</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {/* Airline PNR */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3">
              <div className="flex items-center gap-2 text-white">
                <Plane className="h-5 w-5" />
                <span className="font-semibold">Airline PNR</span>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-slate-800">{refs.airline_pnr}</code>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => copyToClipboard(refs.airline_pnr, 'Airline PNR')}
                >
                  {copiedRef === 'Airline PNR' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Use this for airline check-in</p>
            </CardContent>
          </Card>

          {/* Hotel Reference */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-3">
              <div className="flex items-center gap-2 text-white">
                <Hotel className="h-5 w-5" />
                <span className="font-semibold">Hotel Reference</span>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-slate-800">{refs.hotel}</code>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => copyToClipboard(refs.hotel, 'Hotel Reference')}
                >
                  {copiedRef === 'Hotel Reference' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Rate Hawk booking reference</p>
            </CardContent>
          </Card>

          {/* Transport Reference */}
          {refs.transport && (
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3">
                <div className="flex items-center gap-2 text-white">
                  <Car className="h-5 w-5" />
                  <span className="font-semibold">Transport</span>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <code className="text-lg font-mono font-bold text-slate-800">{refs.transport}</code>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(refs.transport, 'Transport Reference')}
                  >
                    {copiedRef === 'Transport Reference' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Airport transfer reference</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => toast.info('Confirmation email sent!')}
          >
            <Mail className="h-4 w-4" />
            Email Confirmation
          </Button>
          <Button 
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => toast.info('Downloading itinerary...')}
          >
            <Download className="h-4 w-4" />
            Download Itinerary
          </Button>
          <Button 
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            onClick={onGoHome}
          >
            Back to Home
          </Button>
        </div>

        {/* Important Notes */}
        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-amber-800 mb-2">Important Information</h3>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• A confirmation email has been sent to your registered email address</li>
              <li>• Please save your reference numbers for check-in and hotel arrival</li>
              <li>• Online check-in opens 24-48 hours before departure</li>
              <li>• Contact support if you need to make any changes to your booking</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HolidayBookingSuccess;
